import { useState, useCallback, useRef } from "react";
import { http, downloadAuthenticated } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FileSpreadsheet, FileDown, Play, Table as TableIcon } from "lucide-react";

const REPORTS = [
  { value: "daily_aa_bb", label: "Πωλήσεις Ημέρας (AA – BB – Σύνολο)" },
  { value: "by_category", label: "Πωλήσεις ανά Κατηγορία" },
  { value: "by_product", label: "Πωλήσεις ανά Είδος" },
  { value: "by_vat", label: "Πωλήσεις ανά ΦΠΑ" },
  { value: "by_shift", label: "Πωλήσεις ανά Σταθμό / Βάρδια" },
  { value: "weighable_sales", label: "Πωλήσεις Ζυγιζόμενων Ειδών (ανά κιλό)" },
];

const PERIODS = [
  { value: "today", label: "Σήμερα" },
  { value: "yesterday", label: "Χθες" },
  { value: "this_week", label: "Τρέχουσα Εβδομάδα" },
  { value: "last_week", label: "Προηγούμενη Εβδομάδα" },
  { value: "this_month", label: "Τρέχων Μήνας" },
  { value: "last_month", label: "Προηγούμενος Μήνας" },
  { value: "custom", label: "Ορισμός Περιόδου" },
  { value: "all", label: "Όλες οι Ημερομηνίες" },
];

function periodRange(key) {
  const now = new Date();
  const monday = (d) => {
    const dt = new Date(d);
    const day = (dt.getDay() + 6) % 7;
    dt.setDate(dt.getDate() - day);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };
  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  switch (key) {
    case "today":
      return [startOfDay(now), endOfDay(now)];
    case "yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return [startOfDay(y), endOfDay(y)];
    }
    case "this_week": {
      const s = monday(now);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      return [s, endOfDay(e)];
    }
    case "last_week": {
      const s = monday(now);
      s.setDate(s.getDate() - 7);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      return [s, endOfDay(e)];
    }
    case "this_month":
      return [
        new Date(now.getFullYear(), now.getMonth(), 1),
        endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      ];
    case "last_month":
      return [
        new Date(now.getFullYear(), now.getMonth() - 1, 1),
        endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
      ];
    default:
      return [null, null];
  }
}

function fmtDateISO(d) {
  return d ? d.toISOString() : "";
}

function fmtCell(v, header = "") {
  if (typeof v === "number") {
    // Ιούν 2026: χρηματικές στήλες (€) και ποσοστά (%) ΠΑΝΤΑ με 2 δεκαδικά.
    const h = String(header);
    if (h.includes("€") || h.includes("%")) return v.toFixed(2);
    return Number.isInteger(v) ? v : v.toFixed(2);
  }
  return v ?? "";
}

export default function TabularReportsPanel() {
  const [report, setReport] = useState("daily_aa_bb");
  const [period, setPeriod] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);

  const resolveRange = useCallback(() => {
    if (period === "custom") {
      const f = customFrom ? new Date(customFrom) : null;
      const t = customTo ? new Date(customTo) : null;
      if (t) t.setHours(23, 59, 59, 999);
      return [f, t];
    }
    if (period === "all") return [null, null];
    return periodRange(period);
  }, [period, customFrom, customTo]);

  const queryParams = useCallback(() => {
    const [f, t] = resolveRange();
    const params = { report };
    if (f) params.date_from = fmtDateISO(f);
    if (t) params.date_to = fmtDateISO(t);
    return params;
  }, [report, resolveRange]);

  const run = async () => {
    setLoading(true);
    try {
      const r = await http.get("/stats/tabular", { params: queryParams() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης αναφοράς");
    } finally {
      setLoading(false);
    }
  };

  const onExportXlsx = () => {
    const params = { ...queryParams(), fmt: "xlsx" };
    const stamp = new Date().toISOString().slice(0, 10);
    downloadAuthenticated(
      "/stats/tabular/export",
      params,
      `${report}-${stamp}.xlsx`,
      { folder: "reports" },
    )
      .then((r) => toast.success(
        r.viaCompanion
          ? `Αποθηκεύτηκε: ${r.savedTo}`
          : "Λήφθηκε το XLSX",
      ))
      .catch((e) => toast.error(
        e?.message?.includes("Companion")
          ? "Ο Companion agent δεν αποκρίνεται (πόρτα 9100)"
          : "Αποτυχία εξαγωγής XLSX",
      ));
  };

  const onExportPdf = async () => {
    if (!data || !printRef.current) {
      toast.error("Εκτελέστε πρώτα την αναφορά");
      return;
    }
    setExporting(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      let positionY = margin;
      if (imgH <= pageH - margin * 2) {
        pdf.addImage(imgData, "PNG", margin, positionY, imgW, imgH);
      } else {
        // page-split
        const usableH = pageH - margin * 2;
        let sourceY = 0;
        const sliceH = (canvas.height * usableH) / imgH;
        while (sourceY < canvas.height) {
          const slice = document.createElement("canvas");
          slice.width = canvas.width;
          slice.height = Math.min(sliceH, canvas.height - sourceY);
          slice.getContext("2d").drawImage(
            canvas, 0, sourceY, canvas.width, slice.height,
            0, 0, canvas.width, slice.height,
          );
          const sliceImgH = (slice.height * imgW) / canvas.width;
          pdf.addImage(slice.toDataURL("image/png"), "PNG", margin, positionY, imgW, sliceImgH);
          sourceY += slice.height;
          if (sourceY < canvas.height) {
            pdf.addPage();
            positionY = margin;
          }
        }
      }
      const dt = new Date().toISOString().slice(0, 10);
      const filename = `${data.report}-${dt}.pdf`;
      // Feb 2026 — ship the PDF via Companion agent so kiosk operators
      // actually receive the file (browser downloads are blocked).
      try {
        const buf = pdf.output("arraybuffer");
        const bytes = new Uint8Array(buf);
        let bin = "";
        const CHUNK = 0x8000;
        for (let i = 0; i < bytes.length; i += CHUNK) {
          bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        }
        const b64 = btoa(bin);
        const compResp = await fetch("http://127.0.0.1:9100/save-file", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            folder: "reports",
            filename,
            content_b64: b64,
          }),
        });
        if (!compResp.ok) throw new Error(`Companion HTTP ${compResp.status}`);
        const j = await compResp.json();
        if (!j.ok) throw new Error(j.error || "Companion save failed");
        toast.success(`Αποθηκεύτηκε: ${j.path}`);
      } catch (err) {
        toast.error(
          err?.message?.includes("Companion") || err?.message?.includes("Failed to fetch")
            ? "Ο Companion agent δεν αποκρίνεται (πόρτα 9100)"
            : "Αποτυχία αποθήκευσης PDF",
        );
      }
    } catch (e) {
      toast.error("Αποτυχία εξαγωγής PDF");
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  // Range preview
  const [from, to] = resolveRange();
  const rangeLabel = from || to
    ? `${from ? from.toLocaleDateString("el-GR") : "—"} → ${to ? to.toLocaleDateString("el-GR") : "—"}`
    : "Όλες οι ημερομηνίες";

  const cols = data?.columns || [];
  const rows = data?.rows || [];
  // compute totals for numeric columns (skip first col if string)
  const totals = cols.map((_, i) => {
    if (i === 0) return null;
    let sum = 0;
    let any = false;
    for (const r of rows) {
      const v = r[i];
      if (typeof v === "number") {
        sum += v;
        any = true;
      }
    }
    return any ? sum : null;
  });

  return (
    <Card className="p-5 space-y-4" data-testid="tabular-reports-panel">
      <h4 className="font-heading font-semibold text-lg flex items-center gap-2">
        <TableIcon size={18} /> Αναφορές σε Πίνακα
      </h4>

      {/* Filter bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-xs">Αναφορά</Label>
          <Select value={report} onValueChange={setReport}>
            <SelectTrigger className="h-9" data-testid="tabular-report">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORTS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Περίοδος</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9" data-testid="tabular-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {period === "custom" && (
          <>
            <div>
              <Label className="text-xs">Από</Label>
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9" data-testid="tabular-from" />
            </div>
            <div>
              <Label className="text-xs">Έως</Label>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9" data-testid="tabular-to" />
            </div>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Button onClick={run} disabled={loading} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="tabular-run">
          <Play size={14} className="mr-1.5" />
          {loading ? "Φόρτωση..." : "Εκτέλεση"}
        </Button>
        <Button onClick={onExportXlsx} variant="outline" className="h-9 border-emerald-300 text-emerald-700" data-testid="tabular-xlsx">
          <FileSpreadsheet size={14} className="mr-1.5" /> Εξαγωγή XLSX
        </Button>
        <Button onClick={onExportPdf} disabled={exporting || !data} variant="outline" className="h-9 border-blue-300 text-blue-700" data-testid="tabular-pdf">
          <FileDown size={14} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
          {exporting ? "PDF..." : "Εξαγωγή PDF"}
        </Button>
        <span className="text-[12px] text-slate-500 ml-2">
          <span className="font-bold">Περίοδος:</span> {rangeLabel}
        </span>
      </div>

      {/* Printable area */}
      {data && (
        <div ref={printRef} className="bg-white p-4 border border-slate-200 rounded-md">
          <div className="mb-3">
            <div className="text-lg font-heading font-bold">{data.title}</div>
            <div className="text-xs text-slate-500">
              {rangeLabel} • {data.summary?.row_count || 0} γραμμές
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="tabular-table">
              <thead>
                <tr className="bg-blue-50">
                  {cols.map((c, i) => (
                    <th
                      key={i}
                      className="border border-slate-200 px-2 py-1.5 text-left text-[11px] uppercase tracking-wider font-bold text-slate-700"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={cols.length} className="text-center text-slate-400 py-6 border border-slate-100">
                      Καμία εγγραφή για την επιλεγμένη περίοδο
                    </td>
                  </tr>
                )}
                {rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
                    {r.map((cell, j) => (
                      <td
                        key={j}
                        className={`border border-slate-100 px-2 py-1.5 ${
                          typeof cell === "number" ? "text-right font-mono tabular-nums" : ""
                        }`}
                      >
                        {fmtCell(cell, cols[j])}
                      </td>
                    ))}
                  </tr>
                ))}
                {rows.length > 0 && totals.some((t) => t !== null) && (
                  <tr className="bg-blue-100 font-bold">
                    {cols.map((_, i) => (
                      <td key={i} className="border border-slate-300 px-2 py-1.5 text-right font-mono tabular-nums">
                        {i === 0 ? "ΣΥΝΟΛΑ" : totals[i] !== null ? fmtCell(totals[i], cols[i]) : ""}
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
